/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useMemo } from 'react';
import { useLayout } from '../../context/LayoutContext';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import { getCompanyIdFromToken, getDecodedToken, getTokenFromCookie } from '../../utils/tokenHelper';
import Button from '../../components/Button';
import JobListOffCanvas from '../../components/jobs/JobListOffCanvas';
import JobDetailsOffCanvas from '../../components/jobs/JobDetailsOffCanvas';
import JobListContent from '../../components/jobs/JobListContent';
import QuestionnairesSection from '../../components/questionnaires/QuestionnairesSection';
import { useQuestionnaireManagement } from '../../hooks/useQuestionnaireManagement';
import { useJobManagement } from '../../hooks/useJobManagement';
import { useJobQuestionnaires } from '../../hooks/useJobQuestionnaires';
import { getWithExpiry, setWithExpiry } from '../../utils/sessionStorage';
import Spinner from '../../components/Spinner';

// Available page size options
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const Home = () => {
  const { setHeaderContent } = useLayout();
  const navigate = useNavigate();
  const decodedToken = getDecodedToken();
  const companyId = decodedToken?.companyId || 1 || parseInt(getWithExpiry("companyId"));
  const userId = decodedToken?.userId || null;

  console.log("companyId", companyId);
  console.log("userId", userId);
  
  // Add pagination state
  const [questionnaireCurrentPage, setQuestionnaireCurrentPage] = useState(1);
  const [jobCurrentPage, setJobCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  
  // Selected questionnaire state (shared between hooks)
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(null);
  
  // Use custom hooks for managing different parts of the page
  const { 
    questionnaires, 
    loading, 
    error, 
    handleStatusChange 
  } = useQuestionnaireManagement(companyId, userId);
  
  const {
    apiJobs,
    isLoadingJobs,
    jobsError,
    fetchJobs,
    filteredJobs,
    searchKeyword,
    selectedDivision,
    selectedUser,
    handleSearch,
    isJobDetailsOpen,
    setIsJobDetailsOpen,
    selectedJob,
    handleJobClick,
    handleStaticJobClick,
    closeJobDetails
  } = useJobManagement();
  
  const {
    jobsWithQuestionnaires,
    jobQuestionnaireMap,
    associatingJobs,
    isOffCanvasOpen,
    setIsOffCanvasOpen,
    handleOpenOffCanvas,
    handleCloseOffCanvas,
    handleActionClick,
    getActionButtonText,
    getActionButtonVariant,
    isLoadingJobQuestionnaires
  } = useJobQuestionnaires(apiJobs, selectedQuestionnaireId, setSelectedQuestionnaireId);

  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days*24*60*60*1000));
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
  };

  // Create questionnaire header button
  useEffect(() => {
    setHeaderContent(<Button text="Create Questionnaire" variant="primary" onClick={handleCreateQuestionnaire} id="create-questionnaire-button" />);
  }, [navigate, companyId, setHeaderContent]);

  const handleCreateQuestionnaire = () => {
    setHeaderContent(null);
    console.log('Creating questionnaire');
    navigate('questionnaire-builder');
  };
  
  // Fetch initial data
  useEffect(() => {

    const setCompanyId = () => {
      if(!getWithExpiry("companyId")) {
        setWithExpiry("companyId", companyId?.toString() || "6444", 1000 * 60 * 60 * 24);
        console.log("company id set", companyId?.toString());
      }
    };

    setCompanyId();
    fetchJobs();
  }, [fetchJobs]);
  
  // Preview questionnaire
  const handlePreview = (itemId: string) => {
    console.log(`Previewing questionnaire with ID ${itemId}`);
  };

  // Adapter method for onAddToJob
  const handleAddToJob = (_: number, itemId: string) => {
    handleOpenOffCanvas(itemId);
  };

  // Calculate paginated questionnaires
  const paginatedQuestionnaires = useMemo(() => {
    const startIndex = (questionnaireCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return questionnaires.slice(startIndex, endIndex);
  }, [questionnaires, questionnaireCurrentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(questionnaires.length / itemsPerPage);
  }, [questionnaires.length, itemsPerPage]);

  // Filter jobs to only show eligible ones (not associated with other questionnaires)
  const [isFilteringJobs, setIsFilteringJobs] = useState(false);
  
  const eligibleJobs = useMemo(() => {
    if (!filteredJobs || !jobQuestionnaireMap) return [];
    
    return filteredJobs.filter(job => {
      // Check if job has a questionnaire
      const hasQuestionnaire = jobsWithQuestionnaires.has(job.id);
      
      // If job has a questionnaire, only include it if it's associated with the current questionnaire
      if (hasQuestionnaire) {
        const jobQuestionnaireId = jobQuestionnaireMap.get(job.id);
        return jobQuestionnaireId === selectedQuestionnaireId;
      }
      
      // If job doesn't have a questionnaire, it's eligible
      return true;
    });
  }, [filteredJobs, jobsWithQuestionnaires, jobQuestionnaireMap, selectedQuestionnaireId]);
  
  // Track filtering state with useEffect
  useEffect(() => {
    // Set filtering flag when either main jobs or questionnaire data is loading
    setIsFilteringJobs(isLoadingJobs || isLoadingJobQuestionnaires);
  }, [isLoadingJobs, isLoadingJobQuestionnaires]);
  
  // Update paginatedJobs to use eligibleJobs instead of filteredJobs
  const paginatedJobsEligible = useMemo(() => {
    const startIndex = (jobCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return eligibleJobs.slice(startIndex, endIndex);
  }, [eligibleJobs, jobCurrentPage, itemsPerPage]);
  
  // Add handler for page changes
  const handleJobPageChange = (page: number) => {
    setJobCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setQuestionnaireCurrentPage(1); // Reset to first page when changing page size
    setJobCurrentPage(1);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="mt-2 mb-2 text-center">
        <img 
          src="/svgs/no-results.svg" 
          alt="No questionnaires found" 
        />
      </div>
    );
  }

  return (
    <div>
      <div className='app-container min-vh-100 d-flex flex-column p-3'>
        {/* Questionnaires section */}
        <QuestionnairesSection
          questionnaires={paginatedQuestionnaires}
          onAddToJob={handleAddToJob}
          onPreview={handlePreview}
          onStatusChange={handleStatusChange}
          currentPage={questionnaireCurrentPage}
          totalPages={totalPages}
          onPageChange={setQuestionnaireCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>
      
      {/* Job List Off Canvas */}
      <JobListOffCanvas 
        isOpen={isOffCanvasOpen} 
        onClose={handleCloseOffCanvas}
        title="Select Job"
        totalItems={eligibleJobs?.length || 0}
        itemsPerPage={itemsPerPage}
        onPageChange={handleJobPageChange}
      >
        <JobListContent
          isLoadingJobs={isFilteringJobs}
          isLoadingJobQuestionnaires={false}
          jobsError={jobsError}
          apiJobs={apiJobs}
          filteredJobs={paginatedJobsEligible}
          searchKeyword={searchKeyword}
          selectedDivision={selectedDivision}
          selectedUser={selectedUser}
          fetchJobs={fetchJobs}
          handleSearch={handleSearch}
          handleStaticJobClick={handleStaticJobClick}
          handleJobClick={handleJobClick}
          handleActionClick={handleActionClick}
          getActionButtonText={getActionButtonText}
          getActionButtonVariant={getActionButtonVariant}
          associatingJobs={associatingJobs}
        />
      </JobListOffCanvas>
      
      {/* Job Details Off Canvas Component */}
      <JobDetailsOffCanvas
        isOpen={isJobDetailsOpen}
        onClose={closeJobDetails}
        jobDetails={selectedJob || undefined}
        closeAllOffCanvas={() => {
          setIsJobDetailsOpen(false);
          setIsOffCanvasOpen(false);
        }}
      />
    </div>
  );
};

export default Home;